import React, { useContext, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle,
  ChevronDown, ChevronRight, Loader2, ShieldCheck, Users2, Search, Lock, FileText,
} from 'lucide-react';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const usd = (v) => (v === null || v === undefined
  ? '—'
  : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

const MatchBadge = ({ m }) => {
  if (!m) return null;
  if (m.status === 'matched') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="pi-match-matched">
        <CheckCircle2 size={11} />{m.master_name}{m.payee_type ? ` · ${m.payee_type}` : ''}
      </span>
    );
  }
  if (m.status === 'suggested') {
    const c = (m.candidates || [])[0];
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="pi-match-suggested" title={`${Math.round((c?.score || 0) * 100)}% match — needs confirmation`}>
        <AlertTriangle size={11} />Suggested: {c?.master_name} ({Math.round((c?.score || 0) * 100)}%)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700" data-testid="pi-match-unmatched">
      <XCircle size={11} />No match — confirm manually
    </span>
  );
};

const ReconBadge = ({ r }) => {
  if (r.summary_actual === null || r.summary_actual === undefined) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500" data-testid="pi-recon-none"><AlertTriangle size={11} />No summary total to check</span>;
  }
  if (r.reconciled) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="pi-recon-ok"><CheckCircle2 size={11} />Reconciled</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700" data-testid="pi-recon-variance"><AlertTriangle size={11} />Variance {usd(r.variance)}</span>;
};

const AccountCard = ({ acct }) => {
  const [open, setOpen] = useState(false);
  const r = acct.reconciliation;
  const cm = acct.customer_match;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`pi-account-${acct.account_number || acct.sheet_name}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-testid="pi-account-toggle">
        <div className="flex min-w-0 items-center gap-2">
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          <div className="min-w-0">
            <div className="truncate font-semibold text-[#0E2A47]">{acct.sheet_name} {acct.account_number ? <span className="text-slate-400">#{acct.account_number}</span> : <span className="text-rose-500">· no account #</span>}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
              {cm.status === 'matched'
                ? <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">{cm.name}</span>
                : <span className="rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-600">Account not linked — confirm</span>}
              <span className="text-slate-400">{acct.employees.length} people</span>
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col items-end gap-1">
          <div className="font-bold text-[#0E2A47]">{usd(acct.line_sum)}</div>
          <ReconBadge r={r} />
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-500">
            <span>Line total <b className="text-[#0E2A47]">{usd(acct.line_sum)}</b></span>
            <span>Summary actual <b className="text-[#0E2A47]">{usd(r.summary_actual)}</b></span>
            <span>Variance <b className={r.reconciled ? 'text-emerald-600' : 'text-rose-600'}>{usd(r.variance)}</b></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="pi-emp-table">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400">
                <th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Class</th>
                <th className="py-1 pr-3 text-right">Rate</th><th className="py-1 pr-3 text-right">Units</th>
                <th className="py-1 pr-3 text-right">Gross</th><th className="py-1">Match</th>
              </tr></thead>
              <tbody>
                {acct.employees.map((e, i) => (
                  <tr key={i} className="border-t border-slate-50" data-testid="pi-emp-row">
                    <td className="py-1.5 pr-3 font-medium text-[#0E2A47]">{e.name}
                      {e.class_mismatch && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700">class?</span>}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{e.classification || '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-slate-600">{e.rate != null ? usd(e.rate) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-slate-600">{e.units != null ? e.units : '—'}</td>
                    <td className="py-1.5 pr-3 text-right font-semibold text-[#0E2A47]">{usd(e.gross)}</td>
                    <td className="py-1.5"><MatchBadge m={e.match} /></td>
                  </tr>
                ))}
                {acct.additional.map((a, i) => (
                  <tr key={`a${i}`} className="border-t border-slate-50 bg-amber-50/40" data-testid="pi-addl-row">
                    <td className="py-1.5 pr-3 font-medium text-[#0E2A47]">{a.name} <span className="text-[10px] text-amber-700">additional</span></td>
                    <td className="py-1.5 pr-3 text-slate-500">—</td>
                    <td className="py-1.5 pr-3 text-right text-slate-600">{a.rate != null ? usd(a.rate) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-slate-600">{a.hours != null ? a.hours : '—'}</td>
                    <td className="py-1.5 pr-3 text-right font-semibold text-[#0E2A47]">{usd(a.total)}</td>
                    <td className="py-1.5"><MatchBadge m={a.match} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = 'slate' }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`pi-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-lg font-bold ${tone === 'green' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : tone === 'red' ? 'text-rose-600' : 'text-[#0E2A47]'}`}>{value}</div>
  </div>
);

const PayeeRow = ({ pk, name, candidate, decision, onDecide, token }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const resp = await axios.get(`${API}/payroll/import/reports/payee-search?q=${encodeURIComponent(q)}`, auth(token));
      setResults((resp.data || []).slice(0, 10));
    } catch { setResults([]); } finally { setSearching(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`pr-payee-${pk}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-semibold text-[#0E2A47]">{name}</div>
          <div className="text-xs text-slate-500">
            {decision?.action === 'link' && decision.master_name && <span className="text-emerald-600">→ {decision.master_name} ({decision.payee_type})</span>}
            {decision?.action === 'unlinked' && <span className="text-slate-400">Left unlinked (kept as named payee)</span>}
            {!decision && candidate && <span className="text-amber-600">Suggested: {candidate.master_name} ({Math.round((candidate.score || 0) * 100)}%)</span>}
            {!decision && !candidate && <span className="text-rose-600">No suggestion — search or leave unlinked</span>}
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-1.5">
          {candidate && (
            <button onClick={() => onDecide(pk, { action: 'link', master_id: candidate.master_id, kind: candidate.kind, payee_type: candidate.payee_type, master_name: candidate.master_name })}
              className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white" data-testid={`pr-accept-${pk}`}>Accept</button>
          )}
          <button onClick={() => onDecide(pk, { action: 'unlinked' })}
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600" data-testid={`pr-unlink-${pk}`}>Leave unlinked</button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex flex-1 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1">
          <Search size={13} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            placeholder="Search personnel or vendors…" className="w-full bg-transparent text-xs outline-none" data-testid={`pr-search-${pk}`} />
          <button onClick={doSearch} className="text-xs font-semibold text-[#0E2A47]">{searching ? '…' : 'Go'}</button>
        </div>
      </div>
      {results.length > 0 && (
        <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-100">
          {results.map((r) => (
            <button key={`${r.kind}-${r.master_id}`} onClick={() => { onDecide(pk, { action: 'link', master_id: r.master_id, kind: r.kind, payee_type: r.payee_type, master_name: r.name }); setResults([]); }}
              className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-slate-50" data-testid={`pr-result-${pk}`}>
              <span className="font-medium text-[#0E2A47]">{r.name} {r.active === false && <span className="rounded bg-slate-200 px-1 text-[10px] text-slate-600">inactive</span>}</span>
              <span className="text-slate-400">{r.kind === 'vendor' ? 'Vendor' : r.payee_type}</span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-1.5 text-[11px]">
        <button onClick={() => onDecide(pk, { action: 'create', kind: 'personnel', classification: 'W-2 Employee' })}
          className="rounded border border-slate-300 px-2 py-0.5 text-slate-600" data-testid={`pr-create-w2-${pk}`}>+ Inactive W-2</button>
        <button onClick={() => onDecide(pk, { action: 'create', kind: 'personnel', classification: 'Individual 1099 Contractor' })}
          className="rounded border border-slate-300 px-2 py-0.5 text-slate-600" data-testid={`pr-create-1099-${pk}`}>+ Inactive 1099</button>
        <button onClick={() => onDecide(pk, { action: 'create', kind: 'vendor' })}
          className="rounded border border-slate-300 px-2 py-0.5 text-slate-600" data-testid={`pr-create-vendor-${pk}`}>+ Inactive Vendor</button>
      </div>
    </div>
  );
};

const RegisterView = ({ file, token, onBack }) => {
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const fd = new FormData(); fd.append('file', file);
        const r = await axios.post(`${API}/payroll/import/register-preview`, fd,
          { headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' } });
        setReg(r.data);
      } catch (e) { setErr(e?.response?.data?.detail || 'Could not build register'); }
      finally { setBusy(false); }
    })();
  }, [file, token]);
  const download = async (fmt) => {
    const fd = new FormData(); fd.append('file', file);
    const res = await axios.post(`${API}/payroll/import/register-preview/${fmt}`, fd,
      { headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' }, responseType: 'blob' });
    const href = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = href; a.download = `payroll-register-draft.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(href);
  };
  if (busy) return <div className="p-6 text-sm text-slate-400"><Loader2 className="inline animate-spin" size={14} /> Building register…</div>;
  if (err) return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">{err}</div>;
  const f = reg.flags_summary || {};
  return (
    <div data-testid="pi-register">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="pi-register-back"><ChevronRight size={15} className="rotate-180" />Back</button>
        <div className="flex gap-2">
          <button onClick={() => download('pdf')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-[#0E2A47]" data-testid="pi-register-pdf"><FileText size={14} />PDF</button>
          <button onClick={() => download('docx')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-[#0E2A47]" data-testid="pi-register-docx"><FileText size={14} />Word</button>
        </div>
      </div>
      <h3 className="text-lg font-bold text-[#0E2A47]">Payroll Register — DRAFT (not verified)</h3>
      <p className="mb-1 text-sm text-slate-500">{reg.cadence_label} · Pay period {reg.period?.label} · pay day {reg.period?.pay_day || '—'}</p>
      <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Recorded amounts preserved · {reg.exclusions?.zero_dollar_rows} zero-dollar rows excluded · {f.payee_is_description || 0} work-description payees · {f.unresolved_payee || 0} unresolved payees · {f.account_unresolved || 0} unresolved accounts{f.account_name_mismatch ? ` · ${f.account_name_mismatch} account name mismatch` : ''}{f.possible_cross_sheet_duplicates ? ` · ${f.possible_cross_sheet_duplicates} possible cross-sheet duplicate(s)` : ''}. Not verified until source, screen &amp; PDF agree.</div>

      {reg.accounts.map((a) => (
        <div key={a.sheet_name} className="mb-3 rounded-xl border border-slate-200 bg-white" data-testid="pi-reg-account">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="text-sm font-semibold text-[#0E2A47]">{a.sheet_name} <span className="text-slate-400">#{a.account_number || '—'}</span>
              {a.account_display_name ? <span className="ml-1 text-xs font-normal text-slate-500">→ {a.account_display_name}</span> : <span className="ml-1 text-xs font-normal text-rose-600">→ account unresolved</span>}
              {a.name_mismatch && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] text-rose-700">confirmed name applied</span>}
              {a.title_payee_resolved && <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] text-indigo-700">payee from sheet title</span>}
            </div>
            <div className="text-sm font-bold">{usd(a.subtotal)}</div>
          </div>
          <div className="overflow-x-auto border-t border-slate-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-1">Payee</th><th className="px-2 py-1">Work description</th><th className="px-2 py-1">Basis</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Units</th><th className="px-2 py-1 text-right">Regular</th><th className="px-2 py-1 text-right">Add'l</th><th className="px-2 py-1 text-right">Total</th><th className="px-2 py-1">Source</th></tr></thead>
              <tbody>
                {a.payees.map((p, i) => (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="px-2 py-1">{p.name}
                      {p.flags?.includes('payee_from_title') && <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] text-indigo-700">proposed — confirm</span>}
                      {p.match_status === 'suggested' && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">suggested: {p.match_name}</span>}
                      {p.match_status === 'unmatched' && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] text-rose-700">not in roster</span>}</td>
                    <td className="px-2 py-1 text-slate-500">{p.description || '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{p.pay_basis}{p.pay_basis_inferred && p.pay_basis !== 'additional' ? '*' : ''}</td>
                    <td className="px-2 py-1 text-right">{p.rate ?? '—'}</td>
                    <td className="px-2 py-1 text-right">{p.units ?? '—'}</td>
                    <td className="px-2 py-1 text-right">{usd(p.regular_pay)}</td>
                    <td className="px-2 py-1 text-right">{p.additional_pay ? usd(p.additional_pay) : '—'}</td>
                    <td className="px-2 py-1 text-right font-semibold">{usd(p.total)}</td>
                    <td className="px-2 py-1 text-[10px] text-slate-400">{p.source}</td>
                  </tr>
                ))}
                <tr className="border-t border-slate-200 bg-slate-50 font-semibold"><td className="px-2 py-1" colSpan={7}>Account subtotal</td><td className="px-2 py-1 text-right" colSpan={2}>{usd(a.subtotal)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {reg.work_orders?.length > 0 && (
        <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1 text-sm font-semibold text-[#0E2A47]">Work orders <span className="text-xs font-normal text-slate-400">(payment type — not a customer account)</span></div>
          {reg.work_orders.map((w, i) => (
            <div key={i} className="flex items-center justify-between border-t border-slate-100 py-1 text-xs first:border-0">
              <span>{w.name}
                {w.match_status === 'unmatched' && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] text-rose-700">payee not in roster</span>}
                {w.match_status === 'suggested' && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">suggested: {w.match_name}</span>}
                <span className="ml-1 text-slate-400">· {w.location_customer || w.location}{w.description ? ` · ${w.description}` : ''}</span>
              </span>
              <span className="font-semibold">{usd(w.total)}</span>
            </div>
          ))}
        </div>
      )}

      {reg.cross_sheet_duplicates?.length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="pi-reg-duplicates">
          <div className="mb-1 text-sm font-semibold text-amber-900">Possible cross-sheet duplicates <span className="text-xs font-normal text-amber-700">(review — NOT merged; both still in the total)</span></div>
          {reg.cross_sheet_duplicates.map((d, i) => (
            <div key={i} className="flex items-center justify-between border-t border-amber-200 py-1 text-xs first:border-0">
              <span className="text-amber-900">{d.account} · {d.account_payee}{d.account_description ? ` (${d.account_description})` : ''}
                <span className="mx-1 text-amber-500">↔</span>
                Work order · {d.work_order_payee}{d.work_order_location ? ` · ${d.work_order_location}` : ''}
              </span>
              <span className="font-semibold text-amber-900">{usd(d.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between rounded-xl border-2 border-[#0E2A47] bg-white px-4 py-3">
        <span className="text-sm font-bold text-[#0E2A47]">PAYROLL TOTAL</span>
        <span className="text-lg font-bold text-[#0E2A47]" data-testid="pi-reg-total">{usd(reg.payroll_total)}</span>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="mb-1 text-sm font-semibold text-[#0E2A47]">Reconciliation <span className="text-xs font-normal text-slate-400">(Excel SUMMARY is informational only)</span></div>
        <table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1">Account</th><th className="py-1 text-right">Register</th><th className="py-1 text-right">SUMMARY</th><th className="py-1 text-right">Variance</th></tr></thead>
          <tbody>{reg.reconciliation.map((r, i) => (<tr key={i} className="border-t border-slate-50"><td className="py-1">{r.account}</td><td className="py-1 text-right">{usd(r.register_subtotal)}</td><td className="py-1 text-right text-slate-400">{r.summary_actual == null ? '—' : usd(r.summary_actual)}</td><td className="py-1 text-right">{r.variance == null ? '—' : usd(r.variance)}</td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
};

const ConfirmPanel = ({ preview, file, token, onBack, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [acctRes, setAcctRes] = useState({});
  const [payeeRes, setPayeeRes] = useState({});
  const [paymentDate, setPaymentDate] = useState(preview?.period?.pay_day || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payroll/customers`, auth(token)).then((r) => setCustomers(r.data || [])).catch(() => {});
    // preset account customer matches from the preview
    const a = {};
    preview.accounts.forEach((acc) => {
      if (acc.customer_match?.status === 'matched') a[acc.sheet_name] = { customer_id: acc.customer_match.customer_id };
    });
    setAcctRes(a);
  }, [preview, token]);

  // payees needing review (suggested/unmatched), deduped by sheet|name
  const needReview = [];
  preview.accounts.forEach((acc) => {
    [...acc.employees, ...acc.additional].forEach((e) => {
      if (e.match && e.match.status !== 'matched') {
        needReview.push({ pk: `${acc.sheet_name}|${e.name}`, name: e.name, sheet: acc.sheet_name,
          candidate: (e.match.candidates || [])[0] });
      }
    });
  });
  const unmatchedAccts = preview.accounts.filter((a) => a.customer_match?.status !== 'matched');
  const resolvedCount = needReview.filter((p) => payeeRes[p.pk]).length;

  const commit = async () => {
    setSaving(true); setErr(null);
    try {
      // 'create' decisions are applied post-stage; stage understands link/unlinked.
      const stagePayees = {};
      Object.entries(payeeRes).forEach(([k, d]) => {
        if (d && (d.action === 'link' || d.action === 'unlinked')) stagePayees[k] = d;
      });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('confirm', 'true');
      if (paymentDate) fd.append('payment_date', paymentDate);
      fd.append('resolutions', JSON.stringify({ accounts: acctRes, payees: stagePayees }));
      const res = await axios.post(`${API}/payroll/import/commit`, fd, {
        headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' },
      });
      const importId = res.data.import_id;
      // apply create-inactive decisions on the staged import
      for (const [k, d] of Object.entries(payeeRes)) {
        if (d && d.action === 'create') {
          const nm = k.split('|').slice(1).join('|');
          await axios.post(`${API}/payroll/import/payee/create`,
            { import_id: importId, payee_name: nm, kind: d.kind, classification: d.classification },
            auth(token));
        }
      }
      onSaved({ ...res.data });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not stage.');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="pi-confirm-panel">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2A47]" data-testid="pi-confirm-back"><ChevronRight size={15} className="rotate-180" />Back to preview</button>
      <h3 className="text-lg font-bold text-[#0E2A47]">Review, resolve &amp; stage for approval</h3>
      <p className="mb-4 text-sm text-slate-500">Confirm matches, resolve unknown payees (link to a record — active or inactive — or create an inactive former employee/subcontractor), and set the actual payment date. Recorded amounts are preserved. Staging sends the import to the Global Administrator; it is not locked or counted in YTD until the GA finalizes it.</p>

      <div className="mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <label className="text-sm font-semibold text-[#0E2A47]">Actual payment date</label>
        <input type="date" value={paymentDate || ''} onChange={(e) => setPaymentDate(e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="pi-payment-date" />
        <span className="text-xs text-slate-400">{preview?.period?.pay_day ? 'from the workbook PAY DAY' : 'required — never inferred from the filename or period end'}</span>
      </div>

      {unmatchedAccts.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 text-sm font-semibold text-[#0E2A47]">Accounts to link ({unmatchedAccts.length})</div>
          <div className="space-y-2">
            {unmatchedAccts.map((a) => (
              <div key={a.sheet_name} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid={`pi-acct-link-${a.sheet_name}`}>
                <div className="text-sm font-medium text-[#0E2A47]">{a.sheet_name} {a.account_number && <span className="text-slate-400">#{a.account_number}</span>}</div>
                <select value={acctRes[a.sheet_name]?.customer_id || ''} onChange={(e) => setAcctRes((p) => ({ ...p, [a.sheet_name]: { customer_id: e.target.value } }))}
                  className="rounded-lg border border-slate-300 px-2 py-1 text-xs" data-testid={`pi-acct-select-${a.sheet_name}`}>
                  <option value="">Leave unlinked</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.account_number ? `#${c.account_number} · ` : ''}{c.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="mb-2 text-sm font-semibold text-[#0E2A47]">Payees to confirm ({resolvedCount}/{needReview.length} resolved)</div>
        {needReview.length === 0 ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="pi-allmatched">All payees matched automatically.</div>
        ) : (
          <div className="space-y-2">
            {needReview.map((p) => (
              <PayeeRow key={p.pk} pk={p.pk} name={p.name} candidate={p.candidate}
                decision={payeeRes[p.pk]} token={token}
                onDecide={(pk, d) => setPayeeRes((prev) => ({ ...prev, [pk]: d }))} />
            ))}
          </div>
        )}
      </div>

      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600" data-testid="pi-commit-error">{err}</div>}

      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="text-sm text-slate-500">Unresolved payees can be resolved here or later in Payroll History before the GA finalizes.</div>
        <button onClick={commit} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="pi-save-historical">
          {saving ? <><Loader2 size={15} className="animate-spin" />Staging…</> : <><Lock size={15} />Stage for GA Approval</>}
        </button>
      </div>
    </div>
  );
};

const PayrollImport = ({ meta }) => {
  const { token } = useContext(AuthContext);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('preview');
  const [saved, setSaved] = useState(null);

  const onFile = async (f) => {
    if (!f) return;
    setErr(null); setBusy(true); setPreview(null); setSaved(null); setStep('preview'); setFile(f);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/payroll/import/preview`, fd, {
        headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not read the workbook.');
    } finally {
      setBusy(false);
    }
  };

  if (meta && !meta.can_import) {
    return <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="pi-no-access">You need the Prepare Payroll capability to import historical payroll.</div>;
  }

  const rb = preview?.rollup;
  return (
    <div data-testid="payroll-import">
      <div className="mb-5">
        <h2 className="flex items-center gap-2 text-2xl font-bold text-[#0E2A47]"><UploadCloud size={22} />Import Historical Payroll</h2>
        <p className="text-sm text-slate-500">Upload a PAYROLL MASTER workbook (.xlsx). You'll see a full dry-run preview — parsed amounts, proposed matches and reconciliation — before anything is saved.</p>
      </div>

      <div className="mb-5 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center" data-testid="pi-dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
        <FileSpreadsheet size={32} className="mx-auto text-[#E08A3C]" />
        <p className="mt-3 text-sm font-medium text-slate-600">Drag a payroll workbook here, or</p>
        <button onClick={() => inputRef.current?.click()} disabled={busy}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="pi-choose-file">
          {busy ? <><Loader2 size={15} className="animate-spin" />Reading…</> : <><UploadCloud size={15} />Choose .xlsx file</>}
        </button>
        <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])} data-testid="pi-file-input" />
      </div>

      {err && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600" data-testid="pi-error">{err}</div>}

      {saved && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="pi-saved">
          <div className="flex items-center gap-2 text-amber-700"><CheckCircle2 size={20} /><span className="text-lg font-bold">Staged for Global-Administrator approval</span></div>
          <div className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><div className="text-[11px] uppercase text-slate-400">Cadence</div><div className="font-semibold text-[#0E2A47]">{saved.cadence}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Period</div><div className="font-semibold text-[#0E2A47]">{saved.period?.label || '—'}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Payments</div><div className="font-semibold text-[#0E2A47]">{saved.line_count}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Total gross</div><div className="font-semibold text-[#0E2A47]">{usd(saved.totals?.gross)}</div></div>
          </div>
          <div className="mt-3 text-xs text-amber-800">Not locked and NOT yet in YTD. Excluded from QuickBooks. Open <b>Payroll History</b> to resolve any remaining payees and have the Global Administrator finalize &amp; lock it{saved.flags?.ambiguous_payees ? ` · ${saved.flags.ambiguous_payees} payee(s) still unresolved` : ''}{saved.flags?.uncertain_date ? ' · payment date still required' : ''}.</div>
        </div>
      )}

      {preview && step === 'preview' && (
        <div data-testid="pi-preview">
          <div className="mb-3 rounded-lg bg-blue-50 px-4 py-2 text-xs text-blue-700" data-testid="pi-dryrun-note">
            Dry-run preview — nothing has been saved. Review the matches and reconciliation below, then use Confirm &amp; Import (next step) to save as locked history.
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full bg-[#E08A3C]/15 px-3 py-1 font-semibold text-[#b5651d]" data-testid="pi-cadence">{preview.cadence_label}</span>
            <span className="text-slate-500">Period <b className="text-[#0E2A47]">{preview.period?.label || '—'}</b></span>
            {preview.period?.pay_day && <span className="text-slate-500">Pay day <b className="text-[#0E2A47]">{preview.period.pay_day}</b></span>}
            <span className="text-slate-400">· {preview.filename}</span>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Accounts" value={rb.account_count} />
            <Stat label="Reconciled" value={`${rb.accounts_reconciled}/${rb.account_count}`} tone={rb.accounts_reconciled === rb.account_count ? 'green' : 'amber'} />
            <Stat label="People" value={rb.employee_count} />
            <Stat label="Matched" value={rb.match_counts.matched} tone="green" />
            <Stat label="Need Review" value={rb.match_counts.suggested + rb.match_counts.unmatched} tone="amber" />
            <Stat label="Total Gross" value={usd(rb.total_parsed_gross)} />
          </div>

          {preview.warnings?.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="pi-warnings">
              {preview.warnings.map((w, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{w}</div>)}
            </div>
          )}

          <div className="space-y-2" data-testid="pi-accounts">
            {preview.accounts.map((a) => <AccountCard key={a.sheet_name} acct={a} />)}
          </div>

          <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck size={16} className="text-[#8FC93A]" />
              Next: confirm matches, then save as locked “Historical — Already Paid”.
            </div>
            <button onClick={() => setStep('register')}
              className="inline-flex items-center gap-2 rounded-lg border border-[#0E2A47] px-4 py-2 text-sm font-semibold text-[#0E2A47]" data-testid="pi-view-register">
              <FileText size={15} />View Payroll Register
            </button>
            <button onClick={() => setStep('confirm')}
              className="inline-flex items-center gap-2 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white" data-testid="pi-review-confirm">
              <Users2 size={15} />Review &amp; Confirm Matches
            </button>
          </div>
        </div>
      )}

      {preview && step === 'register' && (
        <RegisterView file={file} token={token} onBack={() => setStep('preview')} />
      )}

      {preview && step === 'confirm' && (
        <ConfirmPanel preview={preview} file={file} token={token}
          onBack={() => setStep('preview')}
          onSaved={(d) => { setSaved(d); setPreview(null); setStep('preview'); }} />
      )}
    </div>
  );
};

export default PayrollImport;
